import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  BanknotesIcon,
  CameraIcon,
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
  MoonIcon,
  PaintBrushIcon,
  ShoppingBagIcon,
  SunIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { formatAmount } from '../utils/currency';
import { initials } from '../components/Layout2/Header2';
import {
  AppPage, BTN_DANGER, BTN_PRIMARY, BTN_SECONDARY, CARD, Empty, Field, INPUT, Pill,
} from '../components/UI/AppUI';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.air-brief.com';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('atpl_access_token')}` });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const TABS = [
  { id: 'profile', label: 'Profile', icon: UserCircleIcon },
  { id: 'purchases', label: 'Purchases', icon: ShoppingBagIcon },
  { id: 'payments', label: 'Payments', icon: BanknotesIcon },
  { id: 'appearance', label: 'Appearance', icon: PaintBrushIcon },
];
// Email is read-only on the server (changing it goes through support)
const PROFILE_FIELDS = ['first_name', 'last_name', 'phone_number', 'region', 'flight_school'];
const CLIQ_STATUS = { pending: ['Pending review', 'amber'], approved: ['Approved', 'green'], rejected: ['Rejected', 'red'] };

const SectionCard = ({ title, description, children, footer }) => (
  <section className={CARD}>
    <div className="border-b border-slate-200 px-5 py-4 sm:px-6 dark:border-white/10">
      <h2 className="font-semibold text-slate-900 dark:text-white">{title}</h2>
      {description && <p className="mt-0.5 text-sm text-slate-500 dark:text-gray-400">{description}</p>}
    </div>
    <div className="p-5 sm:p-6">{children}</div>
    {footer && <div className="flex justify-end gap-3 border-t border-slate-200 px-5 py-4 sm:px-6 dark:border-white/10">{footer}</div>}
  </section>
);

const useList = (path, pick, enabled) => {
  const [state, setState] = useState({ items: [], loading: true });
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    fetch(`${API_URL}${path}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setState({ items: d ? pick(d) : [], loading: false }); })
      .catch(() => { if (!cancelled) setState({ items: [], loading: false }); });
    return () => { cancelled = true; };
  }, [path, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
};

const Spinner = () => <div className="flex h-32 items-center justify-center"><div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary-600" /></div>;

const Settings2 = () => {
  const { user, logout } = useAuth();
  const { theme, changeTheme } = useTheme();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === params.get('tab')) ? params.get('tab') : 'profile';

  const initialForm = () => Object.fromEntries(PROFILE_FIELDS.map((k) => [k, user?.[k] || '']));
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null); // { tone, text }
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { if (user) setForm(initialForm()); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const purchases = useList('/api/payments/purchases/', (d) => d.results || d, tab === 'purchases');
  const cliq = useList('/api/payments/cliq/requests/', (d) => d.requests || [], tab === 'payments');

  const dirty = PROFILE_FIELDS.some((k) => (form[k] || '') !== (user?.[k] || ''));
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setNotice(null);
    try {
      const r = await fetch(`${API_URL}/api/auth/profile/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(form),
      });
      if (r.ok) {
        // AuthContext loads the profile on start, so reload to show the new details everywhere
        window.location.reload();
        return;
      }
      const d = await r.json().catch(() => ({}));
      const first = Object.values(d)[0];
      setNotice({ tone: 'red', text: Array.isArray(first) ? first[0] : 'Your changes could not be saved. Please try again.' });
    } catch {
      setNotice({ tone: 'red', text: 'Your changes could not be saved. Check your connection and try again.' });
    } finally {
      setSaving(false);
    }
  };

  const pickAvatar = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setNotice({ tone: 'red', text: 'Please choose an image file.' }); return; }
    if (file.size > 5 * 1024 * 1024) { setNotice({ tone: 'red', text: 'The photo must be smaller than 5 MB.' }); return; }
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const uploadAvatar = async () => {
    setUploading(true);
    try {
      const body = new FormData();
      body.append('avatar', avatarFile);
      const r = await fetch(`${API_URL}/api/auth/profile/avatar/`, { method: 'POST', headers: authHeaders(), body });
      if (r.ok) { window.location.reload(); return; }
      setNotice({ tone: 'red', text: 'The photo could not be uploaded. Please try another image.' });
    } catch {
      setNotice({ tone: 'red', text: 'The photo could not be uploaded. Check your connection and try again.' });
    } finally {
      setUploading(false);
    }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/delete-account/`, { method: 'DELETE', headers: authHeaders() });
      if (r.ok) {
        localStorage.clear();
        logout();
        navigate('/login', { replace: true, state: { message: 'Account deleted successfully' } });
        return;
      }
      setNotice({ tone: 'red', text: 'Your account could not be deleted. Please try again or contact support.' });
      setDeleteOpen(false);
    } catch {
      setNotice({ tone: 'red', text: 'Your account could not be deleted. Check your connection and try again.' });
    } finally {
      setDeleting(false);
    }
  };

  const avatar = avatarPreview || user?.avatar;

  return (
    <AppPage wide>
      {/* Profile banner */}
      <section className={`overflow-hidden ${CARD}`}>
        <div className="relative h-32 bg-slate-950 sm:h-40">
          <img src={`${API_URL}/media/banners/banner.jpeg`} alt="" className="h-full w-full object-cover object-[65%_center] opacity-80" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/40 to-transparent" />
        </div>
        <div className="flex flex-col gap-4 px-5 pb-5 sm:flex-row sm:items-end sm:px-6">
          <div className="relative -mt-12 flex-shrink-0">
            <button
              onClick={() => fileRef.current?.click()}
              className="group relative block h-24 w-24 overflow-hidden rounded-full border-4 border-white bg-primary-600 dark:border-slate-950"
              aria-label="Change profile photo"
            >
              {avatar
                ? <img src={avatar} alt="" className="h-full w-full object-cover" />
                : <span className="flex h-full w-full items-center justify-center text-2xl font-semibold text-white">{initials(user)}</span>}
              <span className="absolute inset-0 flex items-center justify-center bg-slate-950/60 opacity-0 transition-opacity group-hover:opacity-100">
                <CameraIcon className="h-6 w-6 text-white" />
              </span>
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={pickAvatar} className="hidden" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">{user?.full_name || 'Your account'}</h1>
            <p className="text-sm text-slate-500 dark:text-gray-400">
              {user?.email}{user?.date_joined ? ` · Member since ${fmtDate(user.date_joined)}` : ''}
            </p>
          </div>
          {avatarFile ? (
            <div className="flex gap-2">
              <button onClick={() => { setAvatarFile(null); setAvatarPreview(null); }} className={BTN_SECONDARY}>Cancel</button>
              <button onClick={uploadAvatar} disabled={uploading} className={BTN_PRIMARY}>{uploading ? 'Uploading...' : 'Save photo'}</button>
            </div>
          ) : (
            <button onClick={() => fileRef.current?.click()} className={BTN_SECONDARY}><CameraIcon className="h-4 w-4" />Change photo</button>
          )}
        </div>
      </section>

      {notice && (
        <p role="alert" className={`mt-4 rounded-xl px-4 py-3 text-sm ${notice.tone === 'red' ? 'bg-rose-500/10 text-rose-700 dark:text-rose-300' : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'}`}>
          {notice.text}
        </p>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-[14rem_1fr]">
        {/* Section menu */}
        <nav className="flex gap-1 overflow-x-auto lg:flex-col" aria-label="Settings">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setParams(t.id === 'profile' ? {} : { tab: t.id })}
              aria-current={tab === t.id ? 'page' : undefined}
              className={`flex flex-shrink-0 items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${tab === t.id
                ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200 dark:bg-white/10 dark:text-white dark:ring-0'
                : 'text-slate-500 hover:bg-white hover:text-slate-900 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white'}`}
            >
              <t.icon className="h-5 w-5" />{t.label}
            </button>
          ))}
        </nav>

        <div className="min-w-0 space-y-6">
          {tab === 'profile' && (
            <>
              <form onSubmit={save}>
                <SectionCard
                  title="Personal details"
                  description="Used on your account and when we reply to support requests."
                  footer={(
                    <>
                      <button type="button" onClick={() => setForm(initialForm())} disabled={!dirty || saving} className={`${BTN_SECONDARY} disabled:opacity-50`}>Discard</button>
                      <button type="submit" disabled={!dirty || saving} className={BTN_PRIMARY}>{saving ? 'Saving...' : 'Save changes'}</button>
                    </>
                  )}
                >
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="First name"><input value={form.first_name} onChange={set('first_name')} autoComplete="given-name" className={INPUT} /></Field>
                    <Field label="Last name"><input value={form.last_name} onChange={set('last_name')} autoComplete="family-name" className={INPUT} /></Field>
                    <Field label="Email" hint={<>To change your email, <Link to="/support" className="text-primary-600 hover:underline dark:text-primary-300">contact support</Link>.</>}>
                      <input type="email" value={user?.email || ''} readOnly className={`${INPUT} cursor-not-allowed opacity-70`} />
                    </Field>
                    <Field label="Phone number"><input type="tel" value={form.phone_number} onChange={set('phone_number')} autoComplete="tel" placeholder="+962 7X XXX XXXX" className={INPUT} /></Field>
                    <Field label="Region">
                      <select value={form.region} onChange={set('region')} className={INPUT}>
                        <option value="">Select region</option>
                        <option value="jordan">Jordan</option>
                        <option value="worldwide">Worldwide</option>
                      </select>
                    </Field>
                    <Field label="Flight school"><input value={form.flight_school} onChange={set('flight_school')} placeholder="Your flight school or training centre" className={INPUT} /></Field>
                  </div>
                </SectionCard>
              </form>

              <section className="rounded-2xl border border-rose-500/30 bg-rose-500/[0.04] p-5 sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex gap-3">
                    <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0 text-rose-500" />
                    <div>
                      <h2 className="font-semibold text-slate-900 dark:text-white">Delete account</h2>
                      <p className="mt-0.5 max-w-xl text-sm text-slate-500 dark:text-gray-400">Permanently removes your account, progress and test history. This can&apos;t be undone.</p>
                    </div>
                  </div>
                  <button onClick={() => { setDeleteText(''); setDeleteOpen(true); }} className={BTN_DANGER}>Delete account</button>
                </div>
              </section>
            </>
          )}

          {tab === 'purchases' && (
            <SectionCard title="Purchases" description="Your plans and when they end.">
              {purchases.loading ? <Spinner /> : purchases.items.length === 0 ? (
                <Empty icon={ShoppingBagIcon} title="No purchases yet" action={<Link to="/store" className={BTN_PRIMARY}>See plans</Link>}>
                  Plans you buy will be listed here.
                </Empty>
              ) : (
                <ul className="-my-3 divide-y divide-slate-200 dark:divide-white/10">
                  {purchases.items.map((p) => {
                    const days = p.pricing_tier?.duration_days;
                    const left = p.days_remaining;
                    const usedPct = days && left != null ? Math.min(100, Math.max(0, (1 - left / days) * 100)) : null;
                    return (
                      <li key={p.id} className="py-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-medium text-slate-900 dark:text-white">{p.pricing_tier?.course?.name || p.pricing_tier?.bundle?.name || p.course_name || 'Plan'}</p>
                              <Pill tone={p.is_active ? 'green' : 'gray'}>{p.is_active ? 'Active' : 'Ended'}</Pill>
                            </div>
                            <p className="mt-0.5 text-sm text-slate-500 dark:text-gray-400">
                              {p.pricing_tier?.name || 'Access'} · bought {fmtDate(p.purchased_at || p.created_at)}
                              {p.access_expires_at ? ` · ${p.is_active ? 'until' : 'ended'} ${fmtDate(p.access_expires_at)}` : ''}
                            </p>
                          </div>
                          <p className="font-semibold text-slate-900 tabular-nums dark:text-white">{formatAmount(p.amount_paid ?? p.amount ?? 0, p.currency)}</p>
                        </div>
                        {p.is_active && usedPct !== null && (
                          <div className="mt-3 flex items-center gap-3">
                            <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                              <span className="block h-full rounded-full bg-primary-500" style={{ width: `${usedPct}%` }} />
                            </span>
                            <span className="text-xs text-slate-500 tabular-nums dark:text-gray-400">{left} days left</span>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </SectionCard>
          )}

          {tab === 'payments' && (
            <SectionCard title="CliQ payments" description="Bank transfers you've told us about. We check them within 12 hours.">
              {cliq.loading ? <Spinner /> : cliq.items.length === 0 ? (
                <Empty icon={BanknotesIcon} title="No transfers yet">When you pay by CliQ, the request and its status appear here.</Empty>
              ) : (
                <ul className="-my-3 divide-y divide-slate-200 dark:divide-white/10">
                  {cliq.items.map((q) => {
                    const [label, tone] = CLIQ_STATUS[q.status] || [q.status, 'gray'];
                    return (
                      <li key={q.id} className="flex flex-wrap items-start justify-between gap-3 py-4">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-medium text-slate-900 dark:text-white">{q.product_name}</p>
                            <Pill tone={tone}>{label}</Pill>
                          </div>
                          <p className="mt-0.5 text-sm text-slate-500 dark:text-gray-400">
                            Sent {fmtDate(q.created_at)} to <span className="font-mono">{q.cliq_alias}</span>
                          </p>
                          {q.admin_notes && <p className="mt-2 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-600 dark:bg-white/5 dark:text-gray-300">{q.admin_notes}</p>}
                        </div>
                        <p className="font-semibold text-slate-900 tabular-nums dark:text-white">{q.amount} {q.currency}</p>
                      </li>
                    );
                  })}
                </ul>
              )}
            </SectionCard>
          )}

          {tab === 'appearance' && (
            <SectionCard title="Appearance" description="Choose how AirBrief looks on this device and your account.">
              <div className="grid gap-3 sm:grid-cols-3" role="radiogroup" aria-label="Theme">
                {[
                  { value: 'light', label: 'Light', icon: SunIcon, preview: 'bg-slate-100', bar: 'bg-white', line: 'bg-slate-300' },
                  { value: 'dark', label: 'Dark', icon: MoonIcon, preview: 'bg-slate-950', bar: 'bg-white/10', line: 'bg-white/20' },
                  { value: 'system', label: 'Match device', icon: ComputerDesktopIcon, preview: 'bg-gradient-to-r from-slate-100 from-50% to-slate-950 to-50%', bar: 'bg-white/60', line: 'bg-slate-400/60' },
                ].map((o) => (
                  <button
                    key={o.value}
                    role="radio"
                    aria-checked={theme === o.value}
                    onClick={() => changeTheme(o.value)}
                    className={`overflow-hidden rounded-xl border text-left transition-colors ${theme === o.value ? 'border-primary-500 ring-2 ring-primary-500/30' : 'border-slate-200 hover:border-slate-300 dark:border-white/10 dark:hover:border-white/25'}`}
                  >
                    <span className={`block h-24 p-3 ${o.preview}`}>
                      <span className={`block h-3 w-16 rounded ${o.bar}`} />
                      <span className={`mt-3 block h-2 w-24 rounded ${o.line}`} />
                      <span className={`mt-2 block h-2 w-20 rounded ${o.line}`} />
                    </span>
                    <span className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-slate-900 dark:text-white">
                      <o.icon className="h-4 w-4" />{o.label}
                    </span>
                  </button>
                ))}
              </div>
            </SectionCard>
          )}
        </div>
      </div>

      {deleteOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm" onClick={() => !deleting && setDeleteOpen(false)}>
          <div role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-slate-900">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-rose-500/10"><ExclamationTriangleIcon className="h-6 w-6 text-rose-600" /></span>
            <h2 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">Delete your account?</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">Your progress, test history and any active plans will be lost. Type <strong className="text-slate-900 dark:text-white">DELETE</strong> to confirm.</p>
            <input value={deleteText} onChange={(e) => setDeleteText(e.target.value)} className={`mt-4 ${INPUT}`} placeholder="DELETE" autoFocus />
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setDeleteOpen(false)} disabled={deleting} className={BTN_SECONDARY}>Cancel</button>
              <button onClick={deleteAccount} disabled={deleteText !== 'DELETE' || deleting} className={BTN_DANGER}>{deleting ? 'Deleting...' : 'Delete account'}</button>
            </div>
          </div>
        </div>
      )}
    </AppPage>
  );
};

export default Settings2;
